import { Injectable } from "@angular/core";
import {
  ConvertToInvoicePdfDto,
} from "src/app/utils/ConvertInvoices";
import { PaymentPdfDto } from "../interfaces/payment_pdf_dto";

// Add a TypeScript interface for window object
declare global {
  interface Window {
    SwissQRBill: any;
  }
}

export function mm2pt(millimeters: number): number {
  return millimeters * 2.83465;
}

export async function getBase64ImageFromUrl(imageUrl) {
  var res = await fetch(imageUrl);
  var blob = await res.blob();

  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.addEventListener(
      "load",
      function () {
        resolve(reader.result);
      },
      false
    );

    reader.onerror = () => {
      return reject(this);
    };
    reader.readAsDataURL(blob);
  });
}

@Injectable({
  providedIn: "root",
})
export class QrBillService {
  private SwissQRBill: any = null;
  private scriptLoaded = false;
  private scriptLoading: Promise<void> | null = null;
  
  constructor() {
    // Add preconnect hint for JSDelivr
    this.addPreconnectHint();
  }
  
  private addPreconnectHint() {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://cdn.jsdelivr.net';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
  
  private preloadScript() {
    // Only preload if not already loaded or loading
    if (!this.scriptLoaded && !this.scriptLoading) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = 'https://cdn.jsdelivr.net/npm/swissqrbill/lib/browser/bundle/index.js';
      document.head.appendChild(link);
    }
  }
  
  private loadSwissQRBillScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }
    
    if (this.scriptLoading) {
      return this.scriptLoading;
    }
    
    this.scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://cdn.jsdelivr.net/npm/swissqrbill/lib/browser/bundle/index.js';
      script.async = true;
      script.onload = () => {
        this.SwissQRBill = window.SwissQRBill;
        this.scriptLoaded = true;
        this.scriptLoading = null;
        resolve();
      };
      script.onerror = (error) => {
        this.scriptLoading = null;
        reject(new Error('Failed to load SwissQRBill script'));
      };
      document.body.appendChild(script);
    });
    
    return this.scriptLoading;
  }

  generateQrInvoice(data: any): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.scriptLoaded) {
          await this.loadSwissQRBillScript();
        }
        
        const requestData = ConvertToInvoicePdfDto(data);
        const invoiceData = { ...requestData };
        const hasLogo =
          invoiceData.logo_url !== null &&
          invoiceData.logo_url !== "" &&
          invoiceData.logo_url !== undefined;

        if (
          !invoiceData.debtor.address ||
          !invoiceData.debtor.city ||
          !invoiceData.debtor.zip ||
          !invoiceData.debtor.name
        ) {
          delete invoiceData.debtor;
        }
        delete invoiceData.logo_url;
        delete invoiceData.logo;
        delete invoiceData.logo_alignment;
        delete invoiceData.payments;
        delete invoiceData.rechnungTotal;

        const stream = new this.SwissQRBill.BlobStream();
        const pdf = new this.SwissQRBill.PDF(invoiceData, stream, {
          autoGenerate: false,
          size: "A4",
        });

        if (hasLogo) {
          const logoBase64 = await getBase64ImageFromUrl(requestData.logo_url);

          const img = new Image();
          img.src = logoBase64 as string;
          await new Promise((resolve) => (img.onload = resolve));

          const maxWidth = mm2pt(35);
          const maxHeight = mm2pt(17);

          let width = mm2pt(20);
          let height = (mm2pt(20) * img.height) / img.width;

          if (img.width > img.height) {
            width = maxWidth;
            height = (maxWidth * img.height) / img.width;
            if (height > maxHeight) {
              height = maxHeight;
              width = (maxHeight * img.width) / img.height;
            }
          } else {
            height = maxHeight;
            width = (maxHeight * img.width) / img.height;
            if (width > maxWidth) {
              width = maxWidth;
              height = (maxWidth * img.height) / img.width;
            }
          }

          let x;
          if (requestData.logo_alignment === "right") {
            x = pdf.page.width - width - mm2pt(20);
          } else if (requestData.logo_alignment === "center") {
            x = (pdf.page.width - width) / 2;
          } else {
            x = mm2pt(20);
          }

          pdf.image(logoBase64, x, mm2pt(14), { width, height });
        }
        pdf.fontSize(10);
        pdf.font("Helvetica-Bold");
        pdf.fillColor("black");
        pdf.text(
          requestData.creditor.brand ? requestData.creditor.brand + "\n" : "",
          mm2pt(20),
          hasLogo ? mm2pt(40) : mm2pt(35),
          {
            width: mm2pt(100),
            height: mm2pt(50),
            align: "left",
          }
        );
        pdf.fontSize(12);
        pdf.font("Helvetica");
        pdf.fillColor("black");
        pdf.text(
          requestData.creditor.name +
            "\n" +
            requestData.creditor.address +
            "\n" +
            requestData.creditor.zip +
            " " +
            requestData.creditor.city,
          mm2pt(20),
          requestData.creditor.brand ? mm2pt(45) : mm2pt(35),
          {
            width: mm2pt(100),
            height: mm2pt(50),
            align: "left",
          }
        );

        pdf.fontSize(12);
        pdf.font("Helvetica");
        pdf.text(
          requestData.debtor.name +
            "\n" +
            requestData.debtor.address +
            "\n" +
            requestData.debtor.zip +
            " " +
            requestData.debtor.city,
          mm2pt(130),
          mm2pt(60),
          {
            width: mm2pt(70),
            height: mm2pt(50),
            align: "left",
          }
        );

        pdf.fontSize(14);
        pdf.font("Helvetica-Bold");

        pdf.text(
          (requestData.amount > 0 ? "Rechnung" : "Quittung") +
            " vom " +
            requestData.invoice_date,
          mm2pt(20),
          mm2pt(100),
          {
            width: mm2pt(170),
            align: "left",
          }
        );

        pdf.fontSize(11);
        pdf.font("Helvetica");
        pdf.text(" ", {
          width: mm2pt(170),
          align: "right",
        });

        const table = {
          width: mm2pt(170),
          padding: [5, 10, 5, 5],
          rows: [
            {
              height: 30,
              fillColor: "#DDDDDD",
              font: "Helvetica-Bold",
              fontSize: 9,
              columns: [
                {
                  text: "Pos",
                  width: mm2pt(10),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Datum",
                  width: mm2pt(20),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Anz.",
                  width: mm2pt(10),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
                {
                  text: "Einheit",
                  width: mm2pt(20),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Leistung",
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Einheitspreis",
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
                {
                  text: "Beschreibung",
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Total",
                  width: mm2pt(25),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
              ],
            },
            ...requestData.items.map((item) => ({
              fontSize: 9,
              columns: [
                {
                  text: item.position,
                  width: mm2pt(10),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: item.datum,
                  width: mm2pt(20),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: item.anzahl,
                  width: mm2pt(10),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
                {
                  text: item.einheit,
                  width: mm2pt(20),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: item.bezeichnung,
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: item.einheitspreis,
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
                {
                  text: item.beschreibung,
                  width: mm2pt(30),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: item.total,
                  width: mm2pt(25),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                  borderColor: "#DDDDDD",
                  borderWidth: [undefined, undefined, 1, undefined],
                },
              ],
            })),
            { height: 10, columns: [{ text: "", width: mm2pt(20) }] },
            {
              height: 30,
              columns: [
                { text: "", width: mm2pt(20) },
                { text: "", width: mm2pt(42) },
                { text: "Summe", font: "Helvetica-Bold", fontSize: 9, textOptions: { align: "right" }, width: mm2pt(55) },
                {
                  text: (
                    Math.round(requestData.rechnungTotal * 100) / 100
                  ).toFixed(2),
                  width: mm2pt(58),
                  font: "Helvetica-Bold",
                  padding: [undefined, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
              ],
            },
            ...requestData.payments.map((payment) => ({
              columns: [
                { text: "", width: mm2pt(62) },
                { text: "Zahlung vom " + payment.date, fontSize: 9, width: mm2pt(55), textOptions: { align: "right" } },
                { text: payment.payment_method ?? "", fontSize: 9, width: mm2pt(20), padding: [undefined, undefined, undefined, 15] },
                {
                  text: payment.amount
                    ? (Math.round(payment.amount * 100) / 100).toFixed(2)
                    : "-",
                  width: mm2pt(38),
                  padding: [undefined, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
              ],
            })),
            {
              height: 40,
              columns: [
                { text: "", width: mm2pt(20) },
                { text: "", width: mm2pt(42) },
                {
                  text: "Rechnungstotal",
                  font: "Helvetica-Bold",
                  fontSize: 9,
                  width: mm2pt(55),
                  textOptions: { align: "right" },
                },
                {
                  text: (Math.round(requestData.amount * 100) / 100).toFixed(2),
                  width: mm2pt(58),
                  padding: [undefined, undefined, undefined, -5],
                  textOptions: { align: "right" },
                  font: "Helvetica-Bold",
                },
              ],
            },
          ],
        };

        pdf.addTable(table);
        if (requestData.amount > 0) pdf.addQRBill();

        pdf.end();

        pdf.on("finish", () => {
          resolve(stream.toBlobURL("application/pdf"));
        });

        pdf.on("error", (err) => {
          reject(err);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  generateReceipt(data: PaymentPdfDto): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.scriptLoaded) {
          await this.loadSwissQRBillScript();
        }
        
        const paymentData = { ...data };
        const hasLogo =
          paymentData.logo_url !== null &&
          paymentData.logo_url !== "" &&
          paymentData.logo_url !== undefined;

        delete paymentData.logo_url;
        delete paymentData.logo_alignment;

        const stream = new this.SwissQRBill.BlobStream();
        const pdf = new this.SwissQRBill.PDF(paymentData, stream, {
          autoGenerate: false,
          size: "A4",
        });

        if (hasLogo) {
          const logoBase64 = await getBase64ImageFromUrl(data.logo_url);

          const img = new Image();
          img.src = logoBase64 as string;
          await new Promise((resolve) => (img.onload = resolve));

          const maxWidth = mm2pt(35);
          const maxHeight = mm2pt(17);

          let width = mm2pt(20);
          let height = (mm2pt(20) * img.height) / img.width;

          if (img.width > img.height) {
            width = maxWidth;
            height = (maxWidth * img.height) / img.width;
            if (height > maxHeight) {
              height = maxHeight;
              width = (maxHeight * img.width) / img.height;
            }
          } else {
            height = maxHeight;
            width = (maxHeight * img.width) / img.height;
            if (width > maxWidth) {
              width = maxWidth;
              height = (maxWidth * img.height) / img.width;
            }
          }

          let x;
          if (data.logo_alignment === "right") {
            x = pdf.page.width - width - mm2pt(20);
          } else if (data.logo_alignment === "center") {
            x = (pdf.page.width - width) / 2;
          } else {
            x = mm2pt(20);
          }

          pdf.image(logoBase64, x, mm2pt(14), { width, height });
        }
        pdf.fontSize(12);
        pdf.font("Helvetica-Bold");
        pdf.fillColor("black");
        pdf.text(
          paymentData.creditor.brand ? paymentData.creditor.brand + "\n" : "",
          mm2pt(20),
          mm2pt(35),
          {
            width: mm2pt(100),
            height: mm2pt(50),
            align: "left",
          }
        );
        pdf.fontSize(12);
        pdf.font("Helvetica");
        pdf.fillColor("black");
        pdf.text(
          paymentData.creditor.name +
            "\n" +
            paymentData.creditor.address +
            "\n" +
            paymentData.creditor.zip +
            " " +
            paymentData.creditor.city,
          mm2pt(20),
          paymentData.creditor.brand ? mm2pt(45) : mm2pt(35),
          {
            width: mm2pt(100),
            height: mm2pt(50),
            align: "left",
          }
        );

        pdf.fontSize(12);
        pdf.font("Helvetica");
        pdf.text(
          paymentData.debtor.name +
            "\n" +
            paymentData.debtor.address +
            "\n" +
            paymentData.debtor.zip +
            " " +
            paymentData.debtor.city,
          mm2pt(130),
          mm2pt(60),
          {
            width: mm2pt(70),
            height: mm2pt(50),
            align: "left",
          }
        );

        pdf.fontSize(14);
        pdf.font("Helvetica-Bold");

        pdf.text(
          "Zahlungsbestätigung vom " + paymentData.date,
          mm2pt(20),
          mm2pt(100),
          {
            width: mm2pt(170),
            align: "left",
          }
        );

        pdf.fontSize(1);
        pdf.font("Helvetica");
        pdf.text(" ", {
          width: mm2pt(170),
          align: "right",
        });

        pdf.fontSize(12);
        pdf.font("Helvetica");
        pdf.fillColor("black");
        pdf.text(
          "Danke für deine Zahlung vom " +
            paymentData.date +
            " in Höhe von " +
            paymentData.amount +
            " " +
            paymentData.payment_currency +
            ".",
          mm2pt(20),
          mm2pt(120),
          {
            width: mm2pt(170),
            align: "left",
          }
        );

        pdf.fontSize(1);
        pdf.text(" ", mm2pt(20), mm2pt(140), {
          width: mm2pt(170),
          align: "left",
        });

        const table = {
          width: mm2pt(170),
          padding: [5, 10, 5, 5],
          rows: [
            {
              height: 30,
              fillColor: "#DDDDDD",
              font: "Helvetica-Bold",
              fontSize: 10,
              columns: [
                {
                  text: "Datum",
                  width: mm2pt(55),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Zahlungsmethode",
                  width: mm2pt(60),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: "Total",
                  width: mm2pt(55),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                },
              ],
            },
            {
              fontSize: 10,
              columns: [
                {
                  text: paymentData.date,
                  width: mm2pt(55),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: paymentData.payment_method,
                  width: mm2pt(60),
                  padding: [10, undefined, undefined, undefined],
                },
                {
                  text: paymentData.amount
                    ? (Math.round(paymentData.amount * 100) / 100).toFixed(2)
                    : "-",
                  width: mm2pt(55),
                  padding: [10, undefined, undefined, -5],
                  textOptions: { align: "right" },
                  borderColor: "#DDDDDD",
                  borderWidth: [undefined, undefined, 1, undefined],
                },
              ],
            },
          ],
        };

        pdf.addTable(table);

        pdf.fontSize(11);
        pdf.font("Helvetica");
        pdf.text(" ", {
          width: mm2pt(170),
          align: "right",
        });

        pdf.end();

        pdf.on("finish", () => {
          resolve(stream.toBlobURL("application/pdf"));
        });

        pdf.on("error", (err) => {
          reject(err);
        });
      } catch (error) {
        reject(error);
      }
    });
  }
}
